import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../../../utils/supabase.js";
import { generateRandomPassword } from "../../../utils/passwordGenerator.js";
import { ThemeContext } from "contexts/ThemeContext.jsx";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardImg,
  FormGroup,
  Form,
  Input,
  Container,
  Row,
  Col,
  Alert,
  Spinner,
} from "reactstrap";
// core components
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { serverHost } from "variables/serverdata.js";
import HusariaSelect from "components/HusariaSelect.js";
import Tooltip from "components/ToolTip.js";

function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  // Extract user ID from query parameter
  const searchParams = new URLSearchParams(location.search);
  const userIdParam = searchParams.get("id");
  const isNewUser = searchParams.get("new") === "true";
  const isInvite = searchParams.get("invite") === "true";
  const adminUser = JSON.parse(localStorage.getItem("user"));
  const module_list = [
    { value: "HRMS", label: "HRMS" },
    { value: "HUCS", label: "HUCS" },
  ];
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    mobile_phone: "",
    title: "",
    client_uuid: "",
    user_role: "",
    created_at: "",
    client: null,
    dealer: null,
    user_uuid: null,
    is_new_user: false,
    module_perms: [],
  });
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({
    show: false,
    success: false,
    message: "",
  });
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [isEditingOtherUser, setIsEditingOtherUser] = useState(false);
  const [clientModules, setClientModules] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [inviteData, setInviteData] = useState({
    email: "",
    client_uuid: "",
    site_uuid: "",
  });

  useEffect(() => {
    // Determine if editing other user's profile or creating new user
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (isNewUser) {
      setIsEditingOtherUser(true);
      const initialData = {
        first_name: "",
        last_name: "",
        email_address: "",
        mobile_phone: "",
        title: "",
        client_uuid: "",
        site_uuid: "",
        user_role: "",
        created_at: "",
        client: null,
        dealer: null,
        user_uuid: null,
        is_new_user: true,
        module_perms: [],
      };

      // Set client_uuid and site_uuid based on user role
      if (["SUPERADMIN", "ADMIN", "MGNT"].includes(currentUser.user_role)) {
        const clientId = localStorage.getItem("clientId")
        initialData.client_uuid = clientId;
        initialData.site_uuid = currentUser.site_uuid || "";
        initialData.module_perms = currentUser.module_perms;
        console.log(initialData)
      }

      setUserData(initialData);
      setLoading(false);
      console.log("Initial Client Id: ", initialData.client_uuid);
      // If client_uuid is set, fetch sites for that client
      if (initialData.client_uuid) {
        fetchSites(initialData.client_uuid);
      }
    } else if (userIdParam && currentUser?.user_uuid !== userIdParam) {
      setIsEditingOtherUser(true);
    } else {
      setIsEditingOtherUser(false);
    }
  }, [userIdParam, isNewUser]);

  // Handle file input change
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setSelectedFileName(file.name);
    }
  };

  // Upload avatar file to backend
  const uploadAvatar = async () => {
    const currentUser = JSON.parse(localStorage.getItem("user"));
    if (!selectedFile) {
      alert("Please select a file first");
      return;
    }
    const formData = new FormData();
    formData.append("avatar", selectedFile);

    try {
      const response = await fetch(
        `${serverHost}/upload_avatar/${currentUser.user_uuid}`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (!response.ok) {
        throw new Error("Upload failed");
      }
      const data = await response.json();
      setAvatarUrl(data.path);
      setSaveStatus({
        show: true,
        success: true,
        message: "Avatar uploaded successfully",
      });

      // Update avatar filename in database for current user

      // Update local userData state
      setUserData((prev) => ({ ...prev, avatar: data.path }));

      // Optionally update localStorage user object
      localStorage.setItem(
        "user",
        JSON.stringify({ ...currentUser, avatar: data.path }),
      );
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Avatar upload failed: " + error.message,
      });
    }
  };

  useEffect(() => {
    const fetchModules = async () => {
      try {
        let modules = [];
        if (userData.user_role === "GLOBAL") {
          // Fetch all modules
          const { data, error } = await supabase.from("module").select("*");
          if (error) throw error;
          modules = data || [];
        } else {
          // Fetch client modules and all modules to map names
          if (!userData.client_uuid) {
            setClientModules([]);
            return;
          }
          const { data: clientModulesData, error: clientModulesError } =
            await supabase
              .from("client_module")
              .select("module_uuid")
              .eq("client_uuid", userData.client_uuid);
          if (clientModulesError) throw clientModulesError;

          const { data: allModulesData, error: allModulesError } =
            await supabase.from("module").select("*");
          if (allModulesError) throw allModulesError;

          const clientModuleIds = (clientModulesData || []).map(
            (cm) => cm.module_uuid,
          );
          modules = (allModulesData || []).filter((m) =>
            clientModuleIds.includes(m.module_uuid),
          );
        }
        // Sort modules alphabetically
        modules.sort((a, b) => a.name.localeCompare(b.name));
        setClientModules(modules);
      } catch (error) {
        console.error("Error fetching modules:", error);
        setClientModules([]);
      }
    };

    fetchModules();
  }, [userData.client_uuid, userData.user_role]);

  useEffect(() => {
    const init = async () => {
      // If userIdParam exists and not new user, fetch that user's data, else fetch logged-in user data
      if (userIdParam && !isNewUser) {
        await fetchUserDataById(userIdParam);
      } else if (!isNewUser) {
        await fetchCurrentUserData();
      }
      fetchClients();
    };

    init();
  }, [userIdParam, isNewUser]);

  // Fetch user data by ID
  const fetchUserDataById = async (userId) => {
    setLoading(true);
    try {
      const currentUser = localStorage.getItem("user");
      console.log("Current user: ", currentUser);
      const url = `${serverHost}/fetch_user/${userId}`;
      const resp = await fetch(url, {
        method: "GET",
        headers: {
          Access: currentUser.access_token,
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) console.error("Error fetching user", resp.status);

      const data = (await resp.json()).data;

      console.log("Raw user data from query:", data); // Debug log
      console.log("Site info from query:", data.site); // Debug log

      // If client_uuid exists, fetch sites for that client
      if (data.client_uuid) {
        await fetchSites(data.client_uuid);
      }

      let photo = null;
      // If user role is not GLOBAL and client_uuid exists, fetch client photo
      if (data.user_role !== "GLOBAL" && data.client_uuid) {
        try {
          const { data: clientData, error: clientError } = await supabase
            .from("client")
            .select("photo")
            .eq("client_uuid", data.client_uuid)
            .single();
          if (!clientError && clientData) {
            photo = clientData.photo;
          }
        } catch (error) {
          console.error("Error fetching client photo:", error);
        }
      }

      // Set all user data at once
      const updatedUserData = {
        ...data,
        site_uuid: data.site_uuid || "", // Ensure site_uuid is included
        client: data.client,
        dealer: data.dealer,
        photo: photo,
      };
      console.log(
        "Setting user data with site_uuid:",
        updatedUserData.site_uuid,
      ); // Debug log
      console.log("Full user data:", updatedUserData); // Debug log
      setUserData(updatedUserData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data by ID:", error);
      setLoading(false);
    }
  };

  // Fetch logged-in user data (renamed from fetchUserData)
  const fetchCurrentUserData = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser) {
        console.error("No logged-in user found");
        return;
      }
      await fetchUserDataById(currentUser.user_uuid);
    } catch (error) {
      console.error("Error fetching logged-in user data:", error);
    }
  };

  // Fetch sites function
  const fetchSites = async (clientId) => {
    if (!clientId) {
      setSites([]);
      return;
    }

    try {
      const url = `${serverHost}/fetch_client_sites`;
      const resp = await fetch(url, {
        method: "GET",
        headers: {
          client: clientId,
          "Content-Type": "application/json",
        },
      });
      if (!resp.ok) console.error("Error fetching sites", resp.status);
      const data = (await resp.json()).data;

      console.log("Fetched sites for client", clientId, ":", data); // Debug log
      data.sort((a, b) => a.company_name.localeCompare(b.company_name));
      setSites(data || []);

      // Debug logs for site data
      console.log("Current user site_uuid:", userData.site_uuid);
      console.log(
        "Available sites:",
        data?.map((site) => ({
          id: site.site_uuid,
          name: site.company_name,
        })),
      );
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (!currentUser?.dealer_uuid) {
        console.error("No dealer_uuid found");
        return;
      }
      console.log(currentUser.access_token);
      const url = `${serverHost}/fetch_clients`;
      const resp = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${currentUser.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) console.error("Error fetching client(s) ", resp.status);
      const data = (await resp.json()).data;
      data.sort((a, b) => a.company_name.localeCompare(b.company_name));
      console.log(data);
      setClients(data || []);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleInviteChange = (e) => {
    const { id, value } = e.target;
    const field = id.replace("input-", "");

    setInviteData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // If changing client_uuid, fetch sites and clear site selection
    if (field === "client_uuid") {
      console.log(value);
      fetchSites(value);
      setUserData((prev) => ({
        ...prev,
        site_uuid: "",
      }));
    }

    // If changing role, clear client_uuid and site_uuid based on role
    if (field === "user_role") {
      if (value === "GLOBAL") {
        setUserData((prev) => ({
          ...prev,
          client_uuid: "",
          site_uuid: "",
        }));
      } else if (value === "SUPERADMIN") {
        setUserData((prev) => ({
          ...prev,
          site_uuid: "",
        }));
      }
    }
  };
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const field = id.replace("input-", "");

    setUserData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // If changing client_uuid, fetch sites and clear site selection
    if (field === "client_uuid") {
      console.log(value);
      fetchSites(value);
      setUserData((prev) => ({
        ...prev,
        site_uuid: "",
      }));
    }

    // If changing role, clear client_uuid and site_uuid based on role
    if (field === "user_role") {
      if (value === "GLOBAL") {
        setUserData((prev) => ({
          ...prev,
          client_uuid: "",
          site_uuid: "",
        }));
      } else if (value === "SUPERADMIN") {
        setUserData((prev) => ({
          ...prev,
          site_uuid: "",
        }));
      }
    }
  };

  const handleInviteSubmit = async (e) => {
    console.log("Send Invite Pressed");
    if (!inviteData.email) {
      console.error("invite data invalude");
      return;
    }
    const url = `${serverHost}/send_email`;
    const resp = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        receiver: inviteData.email,
        client_uuid: inviteData.client_uuid,
        site_uuid: inviteData.site_uuid,
        email_type: "invite",
      }),
    });
    return;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (isSaving) return;

    try {
      // Validate required fields
      if (!userData.first_name) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please enter your first name",
        });
        return;
      }

      if (!userData.email_address) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please enter your email address",
        });
        return;
      }

      if (!userData.user_role) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please select a role",
        });
        return;
      }

      // Validate client selection for non-GLOBAL roles
      if (userData.user_role !== "GLOBAL" && !userData.client_uuid) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please select a client (required for non-Global roles)",
        });
        return;
      }

      // Validate site selection for roles other than GLOBAL and SUPERADMIN
      if (
        !["GLOBAL", "SUPERADMIN"].includes(userData.user_role) &&
        !userData.site_uuid
      ) {
        setSaveStatus({
          show: true,
          success: false,
          message: "Please select a site (required for your role)",
        });
        return;
      }

      setIsSaving(true);
      setSaveStatus((prev) => ({ ...prev, show: false }));

      // Check if this is a new user or existing user
      if (userData.is_new_user) {
        // Generate a random password for new user
        const newPassword = generateRandomPassword();

        // Create new user with the generated password
        const currentUser = JSON.parse(localStorage.getItem("user"));
        //hand everything to the backend for proper user registration

        const resp = await fetch(`${serverHost}/register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...userData,
            dealer_uuid: currentUser.dealer_uuid,
          }),
        });

        if (resp.ok) {
          // Send new password via email using backend API on successful user creation
          try {
            const response = await fetch(
              "http://localhost:3020/send-password-reset",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  toEmail: userData.email_address,
                  newPassword: newPassword,
                  emailType: "newUser",
                }),
              },
            );
            if (!response.ok) {
              throw new Error("Failed to send email");
            }
          } catch (emailError) {
            console.error("Failed to send password email:", emailError);
          }
        }

        if (!resp.ok) {
          setSaveStatus({
            show: true,
            success: false,
            message: "Error creating user: " + resp.message,
          });
          return;
        }

        setSaveStatus({
          show: true,
          success: true,
          message: `User created successfully!`,
        });

        // Navigate back to company users page after successful creation
        setTimeout(() => {
          navigate("/admin/companyusers");
        }, 3000);
      } else {
        // Update existing user
        const url = `${serverHost}/update_user/${userData.user_uuid}`;
        const body = {
          first_name: userData.first_name,
          last_name: userData.last_name,
          email_address: userData.email_address,
          mobile_phone: userData.mobile_phone,
          title: userData.title,
          client_uuid: userData.client_uuid || null,
          dealer_uuid: userData.dealer_uuid,
          site_uuid: userData.site_uuid || null,
          user_role: userData.user_role,
          created_at: userData.created_at,
          module_perms: userData.module_perms,
        };
        const resp = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        });

        setSaveStatus({
          show: true,
          success: true,
          message: "Profile updated successfully!",
        });

        // Only update localStorage if editing own profile
        if (!isEditingOtherUser) {
          const currentUser = JSON.parse(localStorage.getItem("user"));
          localStorage.setItem(
            "user",
            JSON.stringify({
              ...currentUser,
              ...userData,
            }),
          );
        }
      }
    } catch (error) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Error saving profile: " + error.message,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading user data...
        </p>
      </div>
    );

  const handleBack = () => navigate("/admin/companyusers");

  return (
    <>
      {isSaving && (
        <div
          role="status"
          aria-live="polite"
          aria-label={
            userData.is_new_user ? "Creating user" : "Saving changes"
          }
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDark
              ? "rgba(15, 23, 42, 0.88)"
              : "rgba(248, 250, 252, 0.88)",
            backdropFilter: "blur(3px)",
          }}
        >
          <div
            className="text-center p-4 rounded shadow"
            style={{
              minWidth: "260px",
              backgroundColor: isDark ? "#1f2937" : "#ffffff",
              color: isDark ? "#e2e8f0" : "#212529",
            }}
          >
            <Spinner color={isDark ? "light" : "primary"} />
            <h4 className="mt-3 mb-1" style={{ color: "inherit" }}>
              {userData.is_new_user ? "Creating user..." : "Saving changes..."}
            </h4>
            <p className="mb-0" style={{ opacity: 0.75 }}>
              {userData.is_new_user
                ? "Please wait while we finish setting up the account."
                : "Please wait while we save your changes."}
            </p>
          </div>
        </div>
      )}

      {isInvite && (
        <>
          <SimpleHeader name={"Invite User"} parentName="Company Users" />
          <Container className="mt--6" fluid>
            <Card className="mb-4">
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
              >
                <h5 className="h3 mb-0">Invite User</h5>
              </CardHeader>
              <CardBody>
                <Form>
                  <Row>
                    <Col lg="4">
                      <FormGroup>
                        <label>Email</label>
                        <Input
                          type="email"
                          placeholder="Enter email"
                          value={inviteData.email}
                          onChange={(e) =>
                            setInviteData({
                              ...inviteData,
                              email: e.target.value,
                            })
                          }
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="4">
                      <FormGroup>
                        <label>Client</label>
                        <Input
                          id="input-client_uuid"
                          type="select"
                          value={inviteData.client_uuid}
                          onChange={handleInviteChange}
                          disabled={adminUser.user_role !== "GLOBAL"} // only GLOBAL can change client
                          required
                        >
                          <option value="">Select Client</option>
                          {clients.map((client) => (
                            <option
                              key={client.client_uuid}
                              value={client.client_uuid}
                            >
                              {client.company_name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col lg="4">
                      <FormGroup>
                        <label>Site</label>
                        <Input
                          id="input-site_uuid"
                          type="select"
                          value={inviteData.site_uuid}
                          onChange={handleInviteChange}
                          disabled={
                            !["GLOBAL", "SUPERADMIN"].includes(
                              adminUser.user_role,
                            )
                          } // only GLOBAL or SUPERADMIN can change site
                          required
                        >
                          <option value="">Select Site</option>
                          {sites.map((site) => (
                            <option key={site.site_uuid} value={site.site_uuid}>
                              {site.company_name}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Button
                    color="primary"
                    onClick={handleInviteSubmit}
                    disabled={
                      !inviteData.email ||
                      !inviteData.client_uuid ||
                      !inviteData.site_uuid
                    }
                  >
                    Send Invite
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Container>
        </>
      )}

      {!isInvite && (
        <>
          <SimpleHeader
            name={
              isEditingOtherUser
                ? userData.is_new_user
                  ? "Create New User"
                  : "Edit User"
                : "Edit My Account"
            }
            parentName="My Account"
          />
          <Container className="mt--6" fluid>
            {saveStatus.show && (
              <Alert
                color={saveStatus.success ? "success" : "danger"}
                isOpen={saveStatus.show}
                toggle={() => setSaveStatus({ ...saveStatus, show: false })}
              >
                {saveStatus.message}
              </Alert>
            )}
            <Row>
              <Col className="order-xl-2" xl="4">
                <Card className="card-profile">
                  <CardImg
                    alt="Client Photo"
                    src={
                      userData.photo && userData.photo !== "null"
                        ? process.env.PUBLIC_URL + userData.photo
                        : require("assets/img/theme/img-1-1000x600.jpg")
                    }
                    top
                  />
                  <Row className="justify-content-center">
                    <Col className="order-lg-2" lg="3">
                      <div className="card-profile-image">
                        <a href="#pablo" onClick={(e) => e.preventDefault()}>
                          <img
                            alt="User Avatar"
                            className="rounded-circle"
                            style={{ height: "90px", width: "90px" }}
                            src={
                              userData.avatar &&
                                userData.avatar !== "null" &&
                                userData.avatar.trim()
                                ? `${serverHost}/avatars/${userData.avatar}?t=${Date.now()}`
                                : require("assets/img/theme/avatar/vue.jpg")
                            }
                          />
                        </a>
                      </div>
                    </Col>
                  </Row>
                  <CardHeader
                    style={{
                      backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                      color: isDark ? "#e2e8f0" : "#111",
                    }}
                    className="text-center border-0 pt-8 pt-md-4 pb-0 pb-md-4"
                  ></CardHeader>
                  <CardBody className="pt-0">
                    <Row>
                      <div className="col">
                        <div className="card-profile-stats d-flex justify-content-center">
                          <div>
                            <span className="heading">
                              {clientModules.length}
                            </span>
                            <span className="description">Husaria Apps</span>
                          </div>
                          <div>
                            <span className="heading">
                              {userData.created_at
                                ? new Date(userData.created_at)
                                  .toISOString()
                                  .slice(0, 10)
                                : "N/A"}
                            </span>
                            <span className="description">Active Since</span>
                          </div>
                        </div>
                      </div>
                    </Row>
                    <div className="text-center">
                      <h5 className="h3">
                        {userData.first_name} {userData.last_name}
                      </h5>
                      <div className="h5 font-weight-300">
                        <i className="ni location_pin mr-2" />
                        <span>Canada</span>
                      </div>
                      <div className="h5 mt-4">
                        <i className="ni business_briefcase-24 mr-2" />
                        <span>{userData.title || "Not specified"}</span>
                      </div>
                      <div>
                        <i className="ni education_hat mr-2" />
                        {userData.user_role === "GLOBAL"
                          ? userData.dealer?.company_name || "Not specified"
                          : userData.client?.company_name || "Not specified"}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col className="order-xl-1" xl="8">
                <Card>
                  <CardHeader
                    style={{
                      backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                      color: isDark ? "#e2e8f0" : "#111",
                    }}
                  >
                    <Row className="align-items-center">
                      <Col xs="8">
                        <h3 className="mb-0">
                          {userData.is_new_user
                            ? "Create New Account"
                            : "Edit Account"}
                        </h3>
                      </Col>
                      <Col className="text-right" xs="4">
                        <Button color="light" onClick={handleBack} size="sm">
                          Cancel
                        </Button>
                        <Button
                          color="success"
                          onClick={handleSave}
                          size="sm"
                          disabled={isSaving}
                        >
                          {isSaving ? "Saving..." : "Save"}
                        </Button>
                      </Col>
                    </Row>
                  </CardHeader>
                  <CardBody>
                    <Form onSubmit={handleSave}>
                      <h6 className="heading-small text-muted mb-4">
                        User information
                      </h6>
                      <div className="pl-lg-4">
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-first-name"
                              >
                                First name{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <Input
                                id="input-first_name"
                                value={userData.first_name}
                                onChange={handleInputChange}
                                placeholder="First name"
                                required
                                type="text"
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-last-name"
                              >
                                Last name
                              </label>
                              <Input
                                id="input-last_name"
                                value={userData.last_name}
                                onChange={handleInputChange}
                                placeholder="Last name"
                                type="text"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <div className="d-flex align-items-center mb-2">
                                <label
                                  className="form-control-label"
                                  htmlFor="input-email"
                                >
                                  Email <span className="text-danger">*</span>
                                </label>
                                <Tooltip text="Will be used as the users login name.">
                                  <label
                                    style={{
                                      marginLeft: "10px",
                                      fontSize: "12px",
                                      fontWeight: "bold",
                                      paddingBottom: "5px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ?
                                  </label>
                                </Tooltip>
                              </div>
                              <Input
                                id="input-email_address"
                                value={userData.email_address}
                                onChange={handleInputChange}
                                placeholder="Email address"
                                required
                                type="email"
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-mobile-phone"
                              >
                                Mobile Phone Number
                              </label>
                              <Tooltip text="Will be used for SMS updates.">
                                <label
                                  style={{
                                    marginLeft: "10px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    paddingBottom: "5px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ?
                                </label>
                              </Tooltip>
                              <Input
                                id="input-mobile_phone"
                                value={userData.mobile_phone}
                                onChange={handleInputChange}
                                placeholder="+1.604.555.1234"
                                type="text"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                      <h6 className="heading-small text-muted mb-4">
                        User Permissions
                      </h6>
                      <div className="pl-lg-4">
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-user_role"
                              >
                                Role <span className="text-danger">*</span>
                              </label>
                              <Tooltip text="The user's access level.">
                                <label
                                  style={{
                                    marginLeft: "10px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    paddingBottom: "5px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ?
                                </label>
                              </Tooltip>
                              <Input
                                id="input-user_role"
                                type="select"
                                value={userData.user_role}
                                onChange={handleInputChange}
                                required
                                disabled={!isEditingOtherUser}
                              >
                                <option value="">Select Role</option>
                                {(() => {
                                  const currentUser = JSON.parse(
                                    localStorage.getItem("user"),
                                  );
                                  const isSelfEdit = !isEditingOtherUser;
                                  const protectedRoles = [
                                    "GLOBAL",
                                    "TECH",
                                    "SALES",
                                  ];
                                  // GLOBAL users can assign any role
                                  if (currentUser?.user_role === "GLOBAL") {
                                    if (
                                      !isSelfEdit &&
                                      !protectedRoles.includes(
                                        userData.user_role,
                                      )
                                    ) {
                                      return (
                                        <>
                                          <optgroup label="Distributor">
                                            <option value="GLOBAL">
                                              Dealer
                                            </option>
                                            <option value="TECH">
                                              Technician
                                            </option>
                                            <option value="SALES">Sales</option>
                                          </optgroup>
                                          <optgroup label="Client">
                                            <option value="SUPERADMIN">
                                              Super Admin
                                            </option>
                                            <option value="ADMIN">Admin</option>
                                            <option value="MGNT">
                                              Management
                                            </option>
                                            <option value="USER">User</option>
                                          </optgroup>
                                        </>
                                      );
                                    }
                                    return (
                                      <>
                                        <optgroup label="Distributor">
                                          <option value="GLOBAL">Dealer</option>
                                          <option value="TECH">
                                            Technician
                                          </option>
                                          <option value="SALES">Sales</option>
                                        </optgroup>
                                        <optgroup label="Client">
                                          <option value="SUPERADMIN">
                                            Super Admin
                                          </option>
                                          <option value="ADMIN">Admin</option>
                                          <option value="MGNT">
                                            Management
                                          </option>
                                          <option value="USER">User</option>
                                        </optgroup>
                                      </>
                                    );
                                  }

                                  // SUPERADMIN users
                                  if (currentUser?.user_role === "SUPERADMIN") {
                                    // Editing others - same options as self-edit
                                    return (
                                      <>
                                        <option value="SUPERADMIN">
                                          Super Admin
                                        </option>
                                        <option value="ADMIN">Admin</option>
                                        <option value="MGNT">Management</option>
                                        <option value="USER">User</option>
                                      </>
                                    );
                                  }

                                  // ADMIN users
                                  if (currentUser?.user_role === "ADMIN") {
                                    return (
                                      <>
                                        <option value="ADMIN">Admin</option>
                                        <option value="MGNT">Management</option>
                                        <option value="USER">User</option>
                                      </>
                                    );
                                  }

                                  // MGNT users
                                  if (currentUser?.user_role === "MGNT") {
                                    return (
                                      <>
                                        <option value="MGNT">Management</option>
                                        <option value="USER">User</option>
                                      </>
                                    );
                                  }

                                  // USER role - can't change their own role
                                  return <option value="USER">User</option>;
                                })()}
                              </Input>
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <div className="d-flex align-items-center mb-2">
                                <label
                                  className="form-control-label"
                                  htmlFor="input-title"
                                >
                                  Title
                                </label>
                              </div>
                              <Input
                                id="input-title"
                                value={userData.title}
                                onChange={handleInputChange}
                                placeholder="Title"
                                type="text"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-modules"
                              >
                                Accessible Modules
                              </label>
                              <Tooltip text="Leave empty for no access">
                                <label
                                  style={{
                                    marginLeft: "10px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    paddingBottom: "5px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ?
                                </label>
                              </Tooltip>
                              <HusariaSelect
                                isMulti
                                name="module_perms"
                                id="module_perms"
                                isDisabled={
                                  adminUser.user_role !== "GLOBAL" &&
                                  adminUser.user_role !== "SALES" &&
                                  adminUser.user_role !== "TECH"
                                }
                                options={module_list.map((m) => ({
                                  value: m.value,
                                  label: m.label,
                                }))}
                                value={(userData.module_perms || [])
                                  .map((mid) => {
                                    const module = module_list.find(
                                      (ml) => ml.value === mid,
                                    );
                                    return module
                                      ? {
                                        value: module.value,
                                        label: module.label,
                                      }
                                      : null;
                                  })
                                  .filter(Boolean)}
                                onChange={(selectedOptions) => {
                                  const selectedIds = selectedOptions.map(
                                    (opt) => opt.value,
                                  );
                                  console.log(selectedIds);
                                  setUserData((prev) => ({
                                    ...prev,
                                    module_perms: selectedIds,
                                  }));
                                }}
                                style={{ height: "46px" }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                      <h6 className="heading-small text-muted mb-4">
                        Site Assignment
                      </h6>
                      <div className="pl-lg-4">
                        <Row>
                          <Col lg="6">
                            <FormGroup>
                              <div className="d-flex align-items-center mb-2">
                                <label
                                  className="form-control-label"
                                  htmlFor="input-client"
                                >
                                  Client{" "}
                                  {userData.user_role !== "GLOBAL" && (
                                    <span className="text-danger">*</span>
                                  )}
                                </label>
                              </div>
                              {(() => {
                                const currentUser = JSON.parse(
                                  localStorage.getItem("user"),
                                );

                                const disabled =
                                  userData.user_role === "GLOBAL" ||
                                  [
                                    "SUPERADMIN",
                                    "ADMIN",
                                    "MGNT",
                                    "USER",
                                  ].includes(currentUser?.user_role);

                                return (
                                  <Input
                                    id="input-client_uuid"
                                    type="select"
                                    value={userData.client_uuid}
                                    onChange={handleInputChange}
                                    disabled={disabled}
                                    style={{
                                      backgroundColor:
                                        isDark && disabled
                                          ? "#000000ff"
                                          : undefined,
                                    }}
                                    required={userData.user_role !== "GLOBAL"}
                                  >
                                    <option value="">Select Client</option>
                                    {clients.map((client) => (
                                      <option
                                        key={client.client_uuid}
                                        value={client.client_uuid}
                                      >
                                        {client.company_name}
                                      </option>
                                    ))}
                                  </Input>
                                );
                              })()}
                            </FormGroup>
                          </Col>
                          <Col lg="6">
                            <FormGroup>
                              <label
                                className="form-control-label"
                                htmlFor="input-site"
                              >
                                Site{" "}
                                {!["GLOBAL", "SUPERADMIN"].includes(
                                  userData.user_role,
                                ) && <span className="text-danger">*</span>}
                              </label>
                              <Tooltip text="Assign the user to a specific site. Only applicable to Admin level and below.">
                                <label
                                  style={{
                                    marginLeft: "10px",
                                    fontSize: "12px",
                                    fontWeight: "bold",
                                    paddingBottom: "5px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ?
                                </label>
                              </Tooltip>
                              {(() => {
                                const currentUser = JSON.parse(
                                  localStorage.getItem("user"),
                                );

                                const disabled =
                                  userData.user_role === "GLOBAL" ||
                                  [
                                    "SUPERADMIN",
                                    "ADMIN",
                                    "MGNT",
                                    "USER",
                                  ].includes(currentUser?.user_role);

                                return (
                                  <Input
                                    id="input-site_uuid"
                                    type="select"
                                    value={userData.site_uuid}
                                    onChange={handleInputChange}
                                    disabled={disabled}
                                    style={{
                                      backgroundColor:
                                        isDark && disabled
                                          ? "#000000ff"
                                          : undefined,
                                    }}
                                    required={
                                      !["GLOBAL", "SUPERADMIN"].includes(
                                        userData.user_role,
                                      )
                                    }
                                  >
                                    <option value="">Select Site</option>
                                    {sites.length > 0 ? (
                                      sites.map((site) => (
                                        <option
                                          key={site.site_uuid}
                                          value={site.site_uuid}
                                        >
                                          {site.company_name}
                                        </option>
                                      ))
                                    ) : (
                                      <option value="" disabled>
                                        No sites available
                                      </option>
                                    )}
                                  </Input>
                                );
                              })()}
                            </FormGroup>
                          </Col>
                        </Row>
                      </div>
                    </Form>
                  </CardBody>
                </Card>

                {!isEditingOtherUser && (
                  <Card>
                    <CardHeader
                      style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                      }}
                    >
                      <h3 className="mb-0">Upload Avatar</h3>
                    </CardHeader>
                    <CardBody>
                      <Form>
                        <div className="custom-file">
                          <input
                            className="custom-file-input"
                            id="customFileLang"
                            lang="en"
                            type="file"
                            onChange={handleFileChange}
                            style={{ background: "transparent" }}
                          />
                          <label
                            className="custom-file-label"
                            htmlFor="customFileLang"
                          >
                            {selectedFileName || "Select file"}
                          </label>
                        </div>
                      </Form>
                      <Button
                        color="info"
                        size="sm"
                        className="mt-3"
                        onClick={uploadAvatar}
                      >
                        Upload Avatar
                      </Button>
                      {avatarUrl && (
                        <div className="mt-3">
                          <p>Uploaded Avatar Preview:</p>
                          <img
                            src={avatarUrl}
                            alt="Avatar Preview"
                            className="rounded-circle"
                            style={{ width: "100px", height: "100px" }}
                          />
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          </Container>
        </>
      )}
    </>
  );
}

export default Profile;